import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router";
import { ActionButton, AlertDialog, Callout } from "@hamlak/ui";
import { resetBootstrap } from "@/app/bootstrap";
import { LINKS } from "@/config/links";
import { platform } from "@/platform";
import { t } from "@/i18n/ko";
import { useConfigStore } from "@/stores/configStore";
import { paragraphs, useBlockedConfirm } from "./NoticeDialogs";
import { StatusPage } from "./StatusPage";

type Reason = "update" | "maintenance" | "blocked";
const isReason = (v: string | null): v is Reason => v === "update" || v === "maintenance" || v === "blocked";

/**
 * 1.22 `/blocked?reason=update|maintenance|blocked`.
 * update → F09-02(91:3366) 풀페이지, [업데이트하기] → storeUrl 열기 /
 * maintenance → F09-03(91:3385) 풀페이지(제목·본문은 maintenance.title·message 우선), [공지 보기] → 공지사항(노션).
 *   다시 확인 버튼이 없으므로 포그라운드 복귀마다 부트스트랩을 다시 돌려, 점검이 끝났으면 빠져나간다 /
 * blocked(critical) → F06-15 다이얼로그 재표시(NoticeDialogs 와 같은 구성: [확인] → 로그아웃 → /login, [고객센터 문의](주요) → /support).
 */
export function BlockedPage() {
  const [params] = useSearchParams();
  const raw = params.get("reason");
  const reason: Reason = isReason(raw) ? raw : "maintenance";
  if (reason === "blocked") return <AccountBlocked />;
  return <ServiceBlocked reason={reason} />;
}

function ServiceBlocked({ reason }: { reason: "update" | "maintenance" }) {
  const navigate = useNavigate();
  const settings = useConfigStore((s) => s.settings);
  useEffect(() => {
    if (reason !== "maintenance") return;
    return platform.app.onForeground(() => {
      resetBootstrap();
      navigate("/", { replace: true });
    });
  }, [reason, navigate]);
  if (reason === "update") {
    return (
      <StatusPage
        icon="app"
        title={t("blocked.update.title")}
        description={t("blocked.update.body")}
        cta={t("blocked.update.cta")}
        onCta={() => settings?.storeUrl && void platform.browser.open(settings.storeUrl)}
        testId="blocked-update"
      />
    );
  }
  return (
    <StatusPage
      icon="cat"
      title={settings?.maintenance?.title || t("blocked.maintenance.title")}
      description={settings?.maintenance?.message || t("blocked.maintenance.body")}
      cta={t("blocked.maintenance.cta")}
      onCta={() => void platform.browser.open(LINKS.notice)}
      testId="blocked-maintenance"
    />
  );
}

function AccountBlocked() {
  const navigate = useNavigate();
  const [dialogOpen, setDialogOpen] = useState(true);
  const { busy, confirm } = useBlockedConfirm();

  return (
    <main className="page page--bare" data-testid="blocked-blocked">
      <div className="page-body" style={{ display: "flex", flexDirection: "column", gap: 16 }}>
        <Callout tone="critical" title={t("blocked.account.title")} description={t("blocked.account.body")} />
        <ActionButton variant="brandSolid" onClick={() => setDialogOpen(true)}>
          {t("blocked.account.cta")}
        </ActionButton>
        <ActionButton variant="neutralWeak" onClick={() => navigate("/support")}>
          {t("blocked.account.support")}
        </ActionButton>
      </div>
      {/* 시안 F06-15(382:1882): NoticeDialogs 와 같은 neutral pair — 왼쪽 [확인](보조) · 오른쪽 [고객센터 문의](주요), 본문 두 문단 */}
      <AlertDialog
        open={dialogOpen}
        action="pair"
        title={t("blocked.account.title")}
        description={paragraphs(t("notice.blocked.body"))}
        primaryLabel={t("blocked.account.support")}
        secondaryLabel={t("blocked.account.cta")}
        loading={busy}
        onPrimary={() => navigate("/support")}
        onSecondary={() => void confirm()}
        onDismiss={() => setDialogOpen(false)}
      />
    </main>
  );
}
